import { Table } from 'antd'
import type { ColumnsType, TableProps } from 'antd/es/table'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { Trans } from '@/components'
import { TeamRole } from '@/consts/team'
import { formatDatePreset } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { MemberStatus } from '../types/types'
import { RowActionsDropdown } from './RowActionsDropdown'

export type MemberRow = {
  id: string
  name: string
  username: string
  status: MemberStatus
  role: TeamRole
  bindStatus: boolean
  balance: number
  lastLogin: string
}

type MemberTableProps = {
  rows: MemberRow[]
  loading?: boolean

  selectedRowKeys: React.Key[]
  onSelectionChange: (keys: React.Key[]) => void

  onRowAssign: (id: string) => void
  onRowReclaim: (id: string) => void
  onRowSuspendToggle: (id: string, isCurrentlyInactive: boolean) => void
  onRowDelete: (id: string) => void

  sticky?: boolean

  sortKey:
    | keyof Pick<
        MemberRow,
        'id' | 'username' | 'status' | 'role' | 'bindStatus' | 'balance' | 'lastLogin'
      >
    | null
  sortOrder: 'ascend' | 'descend' | null
  onSortChange: (key: MemberTableProps['sortKey'], order: MemberTableProps['sortOrder']) => void
}

export function MemberTable({
  rows,
  loading,
  selectedRowKeys,
  onSelectionChange,
  onRowAssign,
  onRowReclaim,
  onRowSuspendToggle,
  onRowDelete,
  sticky = true,
  sortKey,
  sortOrder,
  onSortChange,
}: MemberTableProps) {
  const columns: ColumnsType<MemberRow> = useMemo(
    () => [
      {
        title: <Trans i18nKey="team.admin.table.id">ID</Trans>,
        dataIndex: 'id',
        key: 'id',
        width: 120,
        fixed: 'left',
        render: (v: string) => <div className="t-sb text-secondary">{v}</div>,
      },
      {
        title: <Trans i18nKey="team.admin.table.name">Name</Trans>,
        dataIndex: 'username',
        key: 'username',
        width: 200,
        render: (v: string) => <div className="text-primary t-sb">{v}</div>,
        sorter: true,
        sortOrder: sortKey === 'username' ? sortOrder : null,
      },
      {
        title: <Trans i18nKey="team.admin.table.status">Status</Trans>,
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (v: MemberRow['status']) => (
          <span
            className={cn('font-semibold', {
              'text-secondary': v === 'inactive',
              'text-success': v === 'active',
            })}
          >
            <Trans i18nKey={`team.member.${v}`}>{v}</Trans>
          </span>
        ),
        sorter: true,
        sortOrder: sortKey === 'status' ? sortOrder : null,
      },
      {
        title: <Trans i18nKey="team.admin.table.role">Role</Trans>,
        dataIndex: 'role',
        key: 'role',
        width: 140,
        align: 'right',
        render: (v: TeamRole) => (
          <span className="t-sb">
            <Trans
              i18nKey={`team.role.${
                v === TeamRole.Owner ? 'owner' : v === TeamRole.Admin ? 'admin' : 'member'
              }`}
            />
          </span>
        ),
        sorter: true,
        sortOrder: sortKey === 'role' ? sortOrder : null,
      },
      {
        title: <Trans i18nKey="team.admin.table.bindStatus">Bind Status</Trans>,
        dataIndex: 'bindStatus',
        key: 'bindStatus',
        width: 160,
        align: 'right',
        render: (v: MemberRow['bindStatus']) => (
          <span
            className={cn('font-semibold', {
              'text-secondary': !v,
              'text-success': v,
            })}
          >
            {v ? (
              <Trans i18nKey="team.member.bound">Bound</Trans>
            ) : (
              <Trans i18nKey="team.member.unbound">Unbound</Trans>
            )}
          </span>
        ),
        sorter: true,
        sortOrder: sortKey === 'bindStatus' ? sortOrder : null,
      },
      {
        title: <Trans i18nKey="team.admin.table.balance">Balance</Trans>,
        dataIndex: 'balance',
        key: 'balance',
        width: 140,
        align: 'right',
        render: (cent: number) => (
          <span className="t-sb tabular-nums">
            {((Number.isFinite(cent) ? cent : 0) / 100).toLocaleString(undefined, {
              minimumFractionDigits: 3,
              maximumFractionDigits: 3,
            })}
          </span>
        ),
        sorter: true,
        sortOrder: sortKey === 'balance' ? sortOrder : null,
      },
      {
        title: <Trans i18nKey="team.admin.table.lastLoginTime">Last Login Time</Trans>,
        dataIndex: 'lastLogin',
        key: 'lastLogin',
        width: 200,
        align: 'right',
        render: (v: string) => <span className="t-sb">{formatDatePreset(v, 'second')}</span>,
        sorter: true,
        sortOrder: sortKey === 'lastLogin' ? sortOrder : null,
      },
      {
        title: <Trans i18nKey="team.admin.table.action">Action</Trans>,
        key: 'action',
        width: 140,
        fixed: 'right',
        align: 'center',
        render: (_: unknown, row: MemberRow) => (
          <div className="inline-flex items-center t-sb">
            <Link
              to={`/team/member/${row.id}`}
              className="text-btn-secondary px-3 inline-flex items-center"
              aria-label={`View details of ${row.username}`}
            >
              <Trans i18nKey="team.admin.table.details">Details</Trans>
            </Link>

            <RowActionsDropdown
              row={row}
              onAssignCredits={onRowAssign}
              onReclaimCredits={onRowReclaim}
              onSuspendToggle={onRowSuspendToggle}
              onDelete={onRowDelete}
            />
          </div>
        ),
      },
    ],
    [onRowAssign, onRowReclaim, onRowSuspendToggle, onRowDelete, sortKey, sortOrder],
  )

  const handleChange: TableProps<MemberRow>['onChange'] = (_p, _f, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    const key = (s?.field ?? s?.columnKey) as MemberTableProps['sortKey']
    const order = (s?.order ?? null) as MemberTableProps['sortOrder']
    onSortChange(key ?? null, order)
  }

  return (
    <Table<MemberRow>
      rowKey="id"
      dataSource={rows}
      columns={columns}
      loading={loading}
      sticky={sticky}
      scroll={{ y: 500 }}
      pagination={false}
      rowSelection={{ selectedRowKeys, onChange: onSelectionChange }}
      onChange={handleChange}
    />
  )
}
